/**
 * MetadataSidebar - Sidebar for displaying and editing metadata fields
 *
 * Features:
 * - Progress bar with automatic calculation
 * - All fields editable inline
 * - Clean and minimalist design
 * - Scroll when needed
 * - Responsive layout
 */

import { useState, useEffect } from 'react'
import { MetadataEditableField } from './MetadataEditableField'
import { Icon } from '@/design-system/atoms/Icon'
import type { MetadataFieldSchema } from '../../config/metadataSchemas'
import { STATUS_COLORS, STATUS_LABELS } from '../../constants/statusColors'

// Helper to convert validation string to RegExp
const convertValidationToRegExp = (validation?: string): RegExp | undefined => {
  if (!validation) return undefined
  try {
    return new RegExp(validation)
  } catch {
    return undefined
  }
}

// Helper to normalize type for MetadataEditableField
const normalizeFieldType = (
  type?: string
): 'text' | 'date' | 'select' | undefined => {
  if (type === 'number') return 'text' // Treat number as text input
  if (type === 'text' || type === 'date' || type === 'select') return type
  return undefined
}

interface MetadataSidebarProps {
  metadata: Record<string, unknown>
  onUpdate: (field: string, value: string) => Promise<void>
  schema: MetadataFieldSchema[]
  status?: string
  onStatusChange?: (value: string) => Promise<void>
  title?: string
  onTitleChange?: (value: string) => Promise<void>

  /* Toggle between History/Metadata */
  onToggleMode?: () => void
  toggleButtonText?: string

  /* Mobile-specific */
  isMobile?: boolean
  onClose?: () => void
}

export const MetadataSidebar = ({
  metadata,
  onUpdate,
  schema,
  status,
  onStatusChange,
  title,
  onTitleChange,
  onToggleMode,
  toggleButtonText,
  isMobile = false,
  onClose,
}: MetadataSidebarProps) => {
  const [titleDraft, setTitleDraft] = useState(title || '')

  useEffect(() => {
    setTitleDraft(title || '')
  }, [title])

  const statusOptions = [
    {
      value: 'in_progress',
      label: STATUS_LABELS.in_progress,
      tone: STATUS_COLORS.in_progress.dot,
    },
    {
      value: 'waiting',
      label: STATUS_LABELS.waiting,
      tone: STATUS_COLORS.waiting.dot,
    },
    {
      value: 'completed',
      label: STATUS_LABELS.completed,
      tone: STATUS_COLORS.completed.dot,
    },
    {
      value: 'cancelled',
      label: STATUS_LABELS.cancelled,
      tone: STATUS_COLORS.cancelled.dot,
    },
  ]

  return (
    <>
      {/* Header */}
      <div
        data-testid="metadata-sidebar-header"
        className="flex-shrink-0 p-5 bg-white/40 dark:bg-gray-800/40 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-gradient-to-b from-brand-500 to-brand-600 rounded-full"></div>
            <h3
              data-testid="metadata-sidebar-title"
              className="text-base font-bold text-gray-900 dark:text-white tracking-tight"
            >
              Dados
            </h3>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle button - lado direito */}
            {onToggleMode && toggleButtonText && (
              <button
                onClick={onToggleMode}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                aria-label={toggleButtonText}
                title={toggleButtonText}
              >
                <Icon name="list" size={20} />
              </button>
            )}

            {/* Close button for mobile drawer */}
            {isMobile && onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                aria-label="Fechar metadados"
              >
                <Icon name="x" size={20} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Fields - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50 dark:bg-gray-900 custom-scrollbar">
        <div className="space-y-5">
          {(title || status) && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm space-y-3">
              {onTitleChange && (
                <MetadataEditableField
                  label="Título"
                  value={titleDraft}
                  fieldKey="conversation_title"
                  onSave={async val => {
                    setTitleDraft(val || '')
                    if (val && onTitleChange) await onTitleChange(val)
                  }}
                  placeholder="Defina um título"
                  type="text"
                />
              )}

              {status && onStatusChange && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Status
                  </p>
                  <div className="relative">
                    <span
                      className={`absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-2.5 w-2.5 rounded-full ${
                        statusOptions.find(opt => opt.value === status)?.tone ||
                        'bg-gray-500'
                      }`}
                    />
                    <select
                      value={status}
                      onChange={e => onStatusChange(e.target.value)}
                      className="w-full appearance-none pl-8 pr-8 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                      <Icon name="chevron-down" size={16} />
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm space-y-3">
            <div className="space-y-4">
              {schema.map(field => (
                <MetadataEditableField
                  key={field.key}
                  label={field.label}
                  value={(metadata[field.key] as string) || null}
                  fieldKey={field.key}
                  onSave={value => onUpdate(field.key, value)}
                  placeholder={field.placeholder}
                  validation={convertValidationToRegExp(field.validation)}
                  validationMessage={field.validationMessage}
                  type={normalizeFieldType(field.type)}
                  options={field.options}
                  required={field.required}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
